import { useCallback, useState } from "react";
import { useMultiSiteContext } from "@hooks/use-site-context";
import { useUserContext } from "@hooks/use-auth-context";
import { useRefreshTokenMutation } from "@hooks/use-refresh-token-mutation";
import { fetchWithTokenRefresh } from "@hooks/shared/fetch-with-token-refresh";

interface DeleteReportConfigResponse {
  id?: string;
  user_id?: number;
  success?: boolean;
  message?: string;
}

/**
 * Hook to delete/reset user report configuration
 * Calls: DELETE /wp-json/smassets/v1/reports/{reportId}/user-config
 */
export const useDeleteReportUserConfig = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { user } = useUserContext();
  const { getApiUrl } = useMultiSiteContext();
  const { refreshUserToken } = useRefreshTokenMutation();

  const deleteUserConfig = useCallback(
    async (reportId: string): Promise<DeleteReportConfigResponse | null> => {
      setLoading(true);
      setError(null);

      try {
        const url = getApiUrl(`wp-json/smassets/v1/reports/${reportId}/user-config`);

        const response = await fetchWithTokenRefresh(
          url,
          {
            method: "DELETE",
            headers: {
              "Authorization": `Bearer ${user?.authToken ?? ""}`,
              "Content-Type": "application/json",
            },
          },
          () => refreshUserToken(user?.refreshToken ?? "")
        );

        if (!response.ok) {
          throw new Error(`Failed to delete report configuration: ${response.status}`);
        }

        const result = await response.json();
        setLoading(false);
        return result as DeleteReportConfigResponse;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to delete report configuration";
        setError(errorMessage);
        setLoading(false);
        return null;
      }
    },
    [user?.authToken, user?.refreshToken, getApiUrl, refreshUserToken]
  );

  return {
    deleteUserConfig,
    loading,
    error,
  };
};
